import { Component, computed, effect, input, inject } from '@angular/core';
import { IInstrumentChartOut, IChartOptions, IChartPointValue } from 'src/models/charts.model';
import { ECurrencyCode } from 'src/models/common.model';
import { PricePipe } from 'src/pipes/price.pipe';
import { setupHistoryDevelopmentChartOverlay, updateHistoryDevelopmentChartOverlay } from 'src/util/charts.config';

@Component({
  selector: 'liberro-instrument-chart',
  standalone: false,
  templateUrl: './liberro-instrument-chart.component.html',
  styleUrl: './liberro-instrument-chart.component.less'
})
export class LiberroInstrumentChartComponent {
  private pricePipe = inject(PricePipe);

  public chartData = input.required<IInstrumentChartOut>();
  public hasErrored = input.required<boolean>();
  public noDataToDisplay = computed(() => !this.chartData() || !this.chartData().priceInTime.length);
  public chartOptions?: IChartOptions;

  public constructor() {
    effect(() => {
      const chartData = this.chartData();
      if (chartData) {
        this.updateChart(chartData.priceInTime);
      }
    });
  }

  private updateChart(chartData: IChartPointValue[]): void {
    const min = Math.min(...chartData.map(p => p.y));
    const max = Math.max(...chartData.map(p => p.y));
    const chartColor = chartData[0].y > chartData[chartData.length - 1].y ? '#BE1817' : '#3D8146';

    this.chartOptions = {
      series: [{
        name: '',
        data: chartData.map(d => ({ x: d.x, y: d.y }))
      }],
      legend: {
        show: false,
      },
      markers: {
          size: [1, 5],
          colors: ['transparent', '#3D8146'],
          strokeColors: '#3D8146',
          shape: 'square',
          strokeWidth: 0,
          strokeDashArray: 0,
          hover: {
              sizeOffset: 1,
              size: 1,
          },
      },
      chart: {
        type: 'area',
        height: 264,
        toolbar: { show: false },
        zoom: { enabled: false },
        events: {
            mounted: setupHistoryDevelopmentChartOverlay,
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth',
        width: 2,
        colors: [chartColor],
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.5,
          opacityTo: 0,
          stops: [0, 100],
          colorStops: [
            {
              offset: 0,
              color: chartColor,
              opacity: 0.5
            },
            {
              offset: 100,
              color: chartColor,
              opacity: 0
            }
          ]
        }
      },
      grid: {
        show: false,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: false } }
      },
      xaxis: {
        type: 'numeric',
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
        crosshairs: { show: false }
      },
      yaxis: {
        tooltip: { enabled: false },
        opposite: true,
        labels: {
          offsetX: -12,
          formatter: (value) => this.pricePipe.transform(value, this.chartData().currency ?? 'CZK') ?? value,
          style: {
            colors: '#6F9BA1',
            fontSize: '11px'
          }
        },
        min: min,
        max: max,
        tickAmount: 7,
      },
    tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        custom: (input) => {
            updateHistoryDevelopmentChartOverlay(input, {
                currencyOverride: (this.chartData().currency ?? 'CZK') as ECurrencyCode,
                toTwoDecimal: true,
                freezeAtEdge: true,
            });
            return '';
        },
    },
    };
  }

}
